import { Button, TableRow, TableCell } from "flowbite-react";
import type { FC } from "react";
import { useState } from "react";
import { HiTrash } from "react-icons/hi";
import type { ContractType } from "../../services/contract-types";
import { SortableTable } from "../../components/SortableTable";
import { sortData, updateSortState } from "../../lib/sorting";
import type { SortState } from "../../lib/sorting";
import { DeleteConfirmationModal } from "./DeleteConfirmationModal";
import { EditContractTypeModal } from "./EditContractTypeModal";

interface ContractTypesTableProps {
  contractTypes: ContractType[];
  onUpdate: () => Promise<void>;
  onManageCommissions: (contractType: ContractType) => void;
}

export const ContractTypesTable: FC<ContractTypesTableProps> = function ({
  contractTypes,
  onUpdate,
  onManageCommissions,
}) {
  const [deleteModal, setDeleteModal] = useState<{
    show: boolean;
    contractType: ContractType | null;
  }>({
    show: false,
    contractType: null,
  });

  const [sortState, setSortState] = useState<SortState<string>>({
    key: "type",
    direction: "asc",
  });

  const handleSort = (key: string) => {
    setSortState((current) => updateSortState(current, key));
  };

  const handleOpenDeleteModal = (contractType: ContractType) => {
    setDeleteModal({
      show: true,
      contractType,
    });
  };

  const handleCloseDeleteModal = () => {
    setDeleteModal({
      show: false,
      contractType: null,
    });
  };

  const columns = [
    { key: "type", label: "Typ", sortable: true },
    { key: "company", label: "Unternehmen", sortable: true },
    { key: "category", label: "Kategorie", sortable: true },
    { key: "description", label: "Beschreibung", sortable: true },
    { key: "active", label: "Status", sortable: true },
    { key: "actions", label: "Aktionen", sortable: false },
  ];

  const sortedContractTypes = sortData(
    contractTypes,
    sortState.key as keyof ContractType,
    sortState.direction
  );

  return (
    <>
      <SortableTable
        columns={columns}
        sortState={sortState}
        onSort={handleSort}
      >
        {sortedContractTypes.map((contractType) => (
          <TableRow
            key={contractType.id}
            className="hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <TableCell className="whitespace-nowrap p-4 text-sm font-normal text-gray-500 dark:text-gray-400">
              <div className="text-base font-semibold text-gray-900 dark:text-white">
                {contractType.type}
              </div>
            </TableCell>
            <TableCell className="whitespace-nowrap p-4 text-base font-medium text-gray-900 dark:text-white">
              {contractType.company}
            </TableCell>
            <TableCell className="whitespace-nowrap p-4 text-base font-medium text-gray-900 dark:text-white">
              {contractType.category}
            </TableCell>
            <TableCell className="p-4 text-base text-gray-900 dark:text-white">
              {contractType.description}
            </TableCell>
            <TableCell className="whitespace-nowrap p-4 text-base font-medium">
              <div
                className={`inline-flex rounded-full px-3 py-1 text-sm font-medium ${
                  contractType.active
                    ? "bg-green-100 text-green-800"
                    : "bg-red-100 text-red-800"
                }`}
              >
                {contractType.active ? "Aktiv" : "Inaktiv"}
              </div>
            </TableCell>
            <TableCell className="space-x-2 whitespace-nowrap p-4">
              <div className="flex items-center gap-x-3">
                <EditContractTypeModal
                  contractType={contractType}
                  onUpdate={onUpdate}
                />
                <Button
                  color="primary"
                  size="sm"
                  onClick={() => onManageCommissions(contractType)}
                >
                  Provisionen
                </Button>
                <Button
                  color="failure"
                  size="sm"
                  onClick={() => handleOpenDeleteModal(contractType)}
                >
                  <HiTrash className="mr-2 text-lg" />
                  Löschen
                </Button>
              </div>
            </TableCell>
          </TableRow>
        ))}
      </SortableTable>
      {deleteModal.show && deleteModal.contractType && (
        <DeleteConfirmationModal
          show={deleteModal.show}
          contractType={deleteModal.contractType}
          onClose={handleCloseDeleteModal}
          onConfirm={onUpdate}
        />
      )}
    </>
  );
};
